import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { GoogleMap, MarkerF, PolylineF, CircleF, useLoadScript } from "@react-google-maps/api";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  ArrowLeft,
  Thermometer,
  RotateCcw,
  Flame,
  Droplets,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  MapPin,
  Loader2,
  Pencil,
  Save,
  Package,
  X,
} from "lucide-react";

type WindrowDetail = {
  windrow: {
    id: number;
    row_id: string;
    name: string;
    location: string | null;
    status: string;
    start_date: string;
    recipe: string | null;
    shape?: "line" | "pile" | null;
    latitude: number | null;
    longitude: number | null;
    end_latitude: number | null;
    end_longitude: number | null;
  };
  temperatures: Array<{
    id: number;
    temperature: number;
    probe_location: string | null;
    photo_url: string | null;
    recorded_by: string | null;
    recorded_at: string;
  }>;
  turns: Array<{
    id: number;
    turn_number: number;
    water_added: boolean;
    equipment: string | null;
    operator: string | null;
    photo_url: string | null;
    turned_at: string;
  }>;
  materials: Array<{
    id: number;
    material_name: string;
    cubic_yards: number | null;
    weight: number | null;
    notes: string | null;
    added_at: string;
  }>;
  compliance: {
    consecutive_days: number;
    required_days: number;
    min_temp: number;
  };
};

const GOOGLE_MAPS_API_KEY = "AIzaSyDZWH9JxBDTkBOGkBhwV7Hjx1F45eTyYIo";

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400" },
  cooking_complete: { label: "Complete", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  ready_for_pickup: { label: "Ready", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  removed: { label: "Removed", color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400" },
};

function feetBetween(aLat: number, aLng: number, bLat: number, bLng: number) {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const earthFeet = 20902231;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthFeet * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function OperatorWindrowDetail() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<WindrowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [gpsMessage, setGpsMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const { isLoaded: isMapPreviewLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  useEffect(() => {
    apiFetch(`/api/windrows/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then(setData)
      .catch(() => navigate("/app/operator"))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  useEffect(() => {
    setLocationDraft(data?.windrow.location || "");
  }, [data?.windrow.location]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 bg-muted rounded w-32 animate-pulse" />
        <div className="h-40 bg-card border border-border rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-28 bg-card border border-border rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { windrow, temperatures, turns, materials = [], compliance } = data;
  const statusConfig = STATUS_CONFIG[windrow.status] || STATUS_CONFIG.active;
  const lastTemp = temperatures.length > 0 ? temperatures[temperatures.length - 1] : null;
  const tempAlert = windrow.status === "active" && lastTemp && lastTemp.temperature < 131;
  const compliancePercent = Math.min(100, (compliance.consecutive_days / compliance.required_days) * 100);

  // Recent entries (last 5)
  const recentTemps = [...temperatures].reverse().slice(0, 5);
  const recentTurns = [...turns].slice(0, 5);
  const isPile = windrow.shape === "pile";
  const hasStartPoint = windrow.latitude != null && windrow.longitude != null;
  const hasEndPoint = windrow.end_latitude != null && windrow.end_longitude != null;
  const mappedLengthFeet =
    hasStartPoint && hasEndPoint
      ? feetBetween(windrow.latitude!, windrow.longitude!, windrow.end_latitude!, windrow.end_longitude!)
      : null;
  const pileDiameterFeet = isPile && mappedLengthFeet != null ? mappedLengthFeet * 2 : null;
  const previewCenter =
    isPile && hasStartPoint
      ? { lat: windrow.latitude!, lng: windrow.longitude! }
      : hasStartPoint && hasEndPoint
        ? { lat: (windrow.latitude! + windrow.end_latitude!) / 2, lng: (windrow.longitude! + windrow.end_longitude!) / 2 }
        : hasStartPoint
          ? { lat: windrow.latitude!, lng: windrow.longitude! }
          : null;
  const inferredPileEdgeA =
    isPile && hasStartPoint && hasEndPoint
      ? {
          lat: windrow.latitude! * 2 - windrow.end_latitude!,
          lng: windrow.longitude! * 2 - windrow.end_longitude!,
        }
      : null;

  const saveLocation = async () => {
    setSavingLocation(true);
    setGpsMessage(null);
    try {
      const res = await apiFetch(`/api/windrows/${windrow.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ location: locationDraft.trim() }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Failed to save location");
      setData((current) =>
        current
          ? {
              ...current,
              windrow: {
                ...current.windrow,
                location: json.windrow?.location || null,
              },
            }
          : current
      );
      setEditingLocation(false);
    } catch (error: any) {
      setGpsMessage({ type: "error", text: error?.message || "Could not save location." });
    } finally {
      setSavingLocation(false);
    }
  };

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <Link
        to="/app/operator"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft size={14} />
        Back
      </Link>

      {/* Windrow info card */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className={`p-4 border-b border-border ${tempAlert ? "bg-red-50 dark:bg-red-900/10" : "bg-forest-50 dark:bg-forest-900/10"}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="heading-display text-xl text-foreground">{windrow.name}</h1>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-mono mt-0.5">{windrow.row_id}</p>
            </div>
            {lastTemp && (
              <div className="text-right">
                <p className={`stat-value text-3xl ${tempAlert ? "text-red-600 dark:text-red-400" : "text-forest-600 dark:text-forest-400"}`}>
                  {lastTemp.temperature}°
                </p>
                <p className="text-[10px] text-muted-foreground">last temp</p>
              </div>
            )}
          </div>
          <div className="mt-3 rounded-xl bg-background/55 border border-border/60 p-3">
            {editingLocation ? (
              <div className="space-y-2">
                <label htmlFor="operator-location" className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  Site Location
                </label>
                <div className="flex gap-2">
                  <input
                    id="operator-location"
                    type="text"
                    value={locationDraft}
                    onChange={(e) => setLocationDraft(e.target.value)}
                    placeholder="Example: Parker Dairy - North Pad"
                    className="min-w-0 flex-1 h-10 px-3 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600"
                  />
                  <button
                    type="button"
                    onClick={saveLocation}
                    disabled={savingLocation}
                    className="h-10 px-3 rounded-lg bg-forest-600 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {savingLocation ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 min-w-0">
                  <MapPin size={12} className="shrink-0" />
                  <span className="truncate">{windrow.location || "No site location set"}</span>
                </p>
                <button
                  type="button"
                  onClick={() => setEditingLocation(true)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-forest-600 dark:text-forest-400 shrink-0"
                >
                  <Pencil size={12} />
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Compliance bar */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck size={12} />
              EPA Compliance
            </span>
            <span className="text-xs font-bold text-foreground">
              {compliance.consecutive_days}/{compliance.required_days} days
            </span>
          </div>
          <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                compliancePercent >= 100 ? "bg-forest-500" : tempAlert ? "bg-red-500" : "bg-amber-500"
              }`}
              style={{ width: `${compliancePercent}%` }}
            />
          </div>
          {compliancePercent >= 100 && (
            <p className="text-[10px] text-forest-600 dark:text-forest-400 font-semibold mt-1.5 flex items-center gap-1">
              <CheckCircle2 size={10} /> Compliance met
            </p>
          )}
        </div>
      </div>

      {/* Quick action buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Link
          to={`/app/operator/log-temp?windrow=${windrow.id}`}
          className="flex flex-col items-center justify-center gap-2 py-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl active:scale-[0.97] transition-transform"
        >
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
            <Flame size={18} className="text-amber-600 dark:text-amber-400" />
          </div>
          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Log Temp</span>
        </Link>
        <Link
          to={`/app/operator/log-turn?windrow=${windrow.id}`}
          className="flex flex-col items-center justify-center gap-2 py-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-2xl active:scale-[0.97] transition-transform"
        >
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <RotateCcw size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <span className="text-xs font-bold text-blue-700 dark:text-blue-400">Log Turn</span>
        </Link>
        <Link
          to={`/app/operator/log-material?windrow=${windrow.id}`}
          className="flex flex-col items-center justify-center gap-2 py-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700 rounded-2xl active:scale-[0.97] transition-transform"
        >
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
            <Package size={18} className="text-amber-600 dark:text-amber-400" />
          </div>
          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Material</span>
        </Link>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="stat-value text-xl text-foreground">{temperatures.length}</p>
          <p className="text-[10px] text-muted-foreground font-semibold">Readings</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="stat-value text-xl text-foreground">{turns.length}</p>
          <p className="text-[10px] text-muted-foreground font-semibold">Turns</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="stat-value text-xl text-foreground">
            {temperatures.length > 0
              ? Math.round(temperatures.reduce((s, t) => s + t.temperature, 0) / temperatures.length)
              : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground font-semibold">Avg °F</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="stat-value text-xl text-foreground">
            {materials.length > 0
              ? Math.round(materials.reduce((s, m) => s + (m.weight || 0), 0) * 10) / 10
              : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground font-semibold">Tons</p>
        </div>
      </div>

      {/* Field GPS mapping */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-1.5">
            <MapPin size={14} className="text-forest-600 dark:text-forest-400" />
            Field Location
          </h2>
        </div>
        <div className="p-4 space-y-3">
          {hasStartPoint && hasEndPoint && previewCenter ? (
            <div className="relative h-40 rounded-xl overflow-hidden border border-border bg-muted/30">
              {isMapPreviewLoaded ? (
                <GoogleMap
                  center={previewCenter}
                  zoom={19}
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  options={{
                    mapTypeId: "satellite",
                    fullscreenControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    clickableIcons: false,
                    gestureHandling: "none",
                    zoomControl: false,
                  }}
                >
                  {!isPile && (
                    <>
                      <MarkerF position={{ lat: windrow.latitude!, lng: windrow.longitude! }} label="A" />
                      <MarkerF position={{ lat: windrow.end_latitude!, lng: windrow.end_longitude! }} label="B" />
                      <PolylineF
                        path={[
                          { lat: windrow.latitude!, lng: windrow.longitude! },
                          { lat: windrow.end_latitude!, lng: windrow.end_longitude! },
                        ]}
                        options={{ strokeColor: "#2d7330", strokeOpacity: 0.95, strokeWeight: 5 }}
                      />
                    </>
                  )}
                  {isPile && (
                    <>
                      <MarkerF position={previewCenter} label="C" />
                      {inferredPileEdgeA && <MarkerF position={inferredPileEdgeA} label="A" />}
                      <MarkerF position={{ lat: windrow.end_latitude!, lng: windrow.end_longitude! }} label="B" />
                      {inferredPileEdgeA && (
                        <PolylineF
                          path={[
                            inferredPileEdgeA,
                            { lat: windrow.end_latitude!, lng: windrow.end_longitude! },
                          ]}
                          options={{ strokeColor: "#c8902a", strokeOpacity: 0.95, strokeWeight: 4 }}
                        />
                      )}
                      <CircleF
                        center={previewCenter}
                        radius={mappedLengthFeet! / 3.28084}
                        options={{
                          fillColor: "#c8902a",
                          fillOpacity: 0.18,
                          strokeColor: "#c8902a",
                          strokeOpacity: 0.95,
                          strokeWeight: 3,
                        }}
                      />
                    </>
                  )}
                </GoogleMap>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                  Loading map preview...
                </div>
              )}
              <div className="absolute inset-x-2 bottom-2 flex items-end justify-between gap-2 pointer-events-none">
                <div className="rounded-lg bg-background/95 border border-border px-2.5 py-1.5 shadow-sm">
                  <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide">
                    {isPile ? "Radius / Diameter" : "Length"}
                  </p>
                  <p className="text-sm font-bold text-foreground">
                    {isPile
                      ? `${Math.round(mappedLengthFeet!)} / ${Math.round(pileDiameterFeet!)} ft`
                      : `${Math.round(mappedLengthFeet!)} ft`}
                  </p>
                </div>
                <Link
                  to={`/app/operator/windrows/${windrow.id}/location`}
                  className="pointer-events-auto inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-lg bg-forest-600 text-white text-sm font-bold shadow-sm active:scale-[0.99] transition"
                >
                  <MapPin size={15} />
                  Edit
                </Link>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border bg-secondary/40 px-3 py-4 text-center space-y-3">
              <p className="text-xs font-semibold text-muted-foreground">No GPS map set</p>
              <Link
                to={`/app/operator/windrows/${windrow.id}/location`}
                className="w-full inline-flex items-center justify-center gap-2 min-h-[44px] rounded-xl bg-forest-600 text-white text-sm font-bold active:scale-[0.99] transition"
              >
                <MapPin size={16} />
                Start GPS Mapping
              </Link>
            </div>
          )}

          {gpsMessage && (
            <div
              className={`rounded-xl px-3 py-2 text-[11px] font-medium ${
                gpsMessage.type === "success"
                  ? "bg-forest-50 text-forest-700 border border-forest-200 dark:bg-forest-900/20 dark:text-forest-300 dark:border-forest-800"
                  : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
              }`}
            >
              {gpsMessage.text}
            </div>
          )}
        </div>
      </div>

      {/* Recent temperatures */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-1.5">
            <Thermometer size={14} className="text-amber-500" />
            Recent Temperatures
          </h2>
          <span className="text-[10px] text-muted-foreground">{temperatures.length} total</span>
        </div>
        {recentTemps.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-xs text-muted-foreground">No readings yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {recentTemps.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    t.temperature < 131 ? "bg-red-100 dark:bg-red-900/30" : "bg-forest-100 dark:bg-forest-900/30"
                  }`}>
                    <Thermometer size={13} className={t.temperature < 131 ? "text-red-500" : "text-forest-600 dark:text-forest-400"} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      {t.temperature}°F
                      {t.probe_location && (
                        <span className="text-muted-foreground font-normal text-[11px] ml-1.5 capitalize">
                          {t.probe_location}
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(t.recorded_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      {t.recorded_by && ` · ${t.recorded_by}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {t.photo_url && (
                    <button onClick={() => setLightboxUrl(t.photo_url)} className="w-8 h-8 rounded-lg overflow-hidden border border-border">
                      <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                    </button>
                  )}
                  {t.temperature < 131 && (
                    <AlertTriangle size={14} className="text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent turns */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-sm font-bold text-foreground flex items-center gap-1.5">
            <RotateCcw size={14} className="text-blue-500" />
            Recent Turns
          </h2>
          <span className="text-[10px] text-muted-foreground">{turns.length} total</span>
        </div>
        {recentTurns.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-xs text-muted-foreground">No turns yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {recentTurns.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <RotateCcw size={13} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      Turn #{t.turn_number}
                      {t.equipment && (
                        <span className="text-muted-foreground font-normal text-[11px] ml-1.5">{t.equipment}</span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(t.turned_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      {t.operator && ` · ${t.operator}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {t.photo_url && (
                    <button onClick={() => setLightboxUrl(t.photo_url)} className="w-8 h-8 rounded-lg overflow-hidden border border-border">
                      <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                    </button>
                  )}
                  {t.water_added && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 dark:text-blue-400 font-bold bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">
                      <Droplets size={9} />
                      Water
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
